import useCustomHook from '@/hooks/useCustomHook';
import useShortcut from '@/hooks/useShortCutHook';
import {
  hasPassedNextProgressStep,
  logSavingProgress,
  minutesSecondsToSeconds,
} from '@/modules/activities/playerTimeAndProgress';
import { useGamificationContext } from '@/shared/Gamification/GamificationContext/GamificationContext';
import { useEffect } from 'react';

const GAMIFICATION_PROGRESS_MILESTONES = [25, 50, 75, 100];
export default function useMediaActivityProgress({
  activity,
  taskProgressActions,
}) {
  const progressPrecission = 1; // percent precission for reporting each activity progress step
  const { state: gamificationState, actions: gamificationActions } =
    useGamificationContext();

  const { state, actions } = useCustomHook({
    defaultState: {
      currentProgress: 0,
      progressStep: 20,
      activityProgressInterval: 0, // is the interval (in milliseconds) between onProgress player events we want for activity progress reporting. We calculate it to be just precise enough to detect the activity progress steps with the specified progressPrecission.
      // It's passed as the progresInterval prop to the react player, unless there is a more demanding interval for detecting cues, for example.
      noSeek: !!taskProgressActions,
      milestones: [],
    },
    actions: {
      onReady: (realDuration) => {
        const taskDuration = activity.data.taskDuration
          ? minutesSecondsToSeconds(activity.data.taskDuration)
          : realDuration;
        const calculatedProgressInterval = Math.floor(
          1000 * taskDuration * (progressPrecission / 100)
        );
        console.log(
          `calculatedProgressInterval (based on duration and percent precission): ${calculatedProgressInterval}ms. ${
            calculatedProgressInterval > 1000
              ? "using 1000ms (cause it's too big)"
              : ''
          }`
        );
        const activityProgressInterval = Math.min(
          // Ensure 1 second time precission as the maximum progress interval if the song is long.
          calculatedProgressInterval,
          1000
        );
        actions.setState({
          taskDuration,
          activityProgressInterval,
        });
        console.log(
          `[ ${activity.type}  taskDuration / realDuration / activityProgressInterval]: ${taskDuration}s / ${realDuration}s / ${activityProgressInterval}ms`
        );
      },
      onStart: async () => {
        if (taskProgressActions) {
          await taskProgressActions.createProgress({
            activityType: activity.type,
            progress: 0,
          });
        }
      },
      onRestart: () => {
        actions.onStart();
        taskProgressActions?.onActivityRestarted();
        actions.setState({
          milestones: [],
          currentTime: 0,
          currentProgress: 0,
        });
      },
      onProgress: async ({ playedSeconds, duration: realDuration }) => {
        const { currentProgress, taskDuration, progressStep } = state;
        const playedPercent = 100 * (playedSeconds / taskDuration);

        if (
          hasPassedNextProgressStep({
            progressStep,
            progressPrecission,
            lastProgress: currentProgress,
            playedPercent,
          }) &&
          currentProgress < 100
        ) {
          const newProgress = Math.min(100, currentProgress + progressStep);
          actions.setState({ currentProgress: newProgress });

          let correctAnswers = [];
          const nextMilestone = GAMIFICATION_PROGRESS_MILESTONES.find(
            (_, index) =>
              state.milestones.length == 0
                ? index == 0
                : index > state.milestones.length - 1
          );
          if (newProgress >= nextMilestone) {
            correctAnswers = [nextMilestone];
            actions.setState({
              milestones: [...state.milestones, nextMilestone],
            });
          }
          await gamificationActions.putData({
            activityId: activity.id,
            progress: newProgress,
            correctAnswers: correctAnswers,
          });

          if (taskProgressActions) {
            logSavingProgress(playedSeconds, taskDuration, realDuration);
            await taskProgressActions.updateProgress({
              activityType: activity.type,
              progress: newProgress,
            });
          }
        }
      },
    },
  });

  useEffect(() => {
    if (gamificationState.isGamificationStudent) {
      actions.setState({
        isGamificationStudent: gamificationState.isGamificationStudent,
        noSeek:
          gamificationState.isGamificationStudent || !!taskProgressActions,
      });
    }
  }, [gamificationState.isGamificationStudent]);

  useShortcut({
    shortcut: 'Ctrl+Shift+Alt+T',
    handler: () => {
      actions.setState({ noSeek: !state.noSeek });
    },
  });

  return { state, actions };
}
